import { arrayMove } from "@dnd-kit/sortable";
import { createId } from "@paralleldrive/cuid2";
import { PlusIcon } from "@phosphor-icons/react";
import { defaultSections, type Skill } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import get from "lodash.get";
import { useMemo, useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";
import { SkillForm } from "./skills/skill-form";
import { SkillList } from "./skills/skill-list";
import { getSkillSuggestions } from "./skills/suggestions";

type View = "list" | "form";
export const SkillsInlineSection = () => {
  const { toast } = useToast();
  const setValue = useResumeStore((state) => state.setValue);
  const headline = useResumeStore((state) => state.resume.data.basics.headline ?? "");
  const section = useResumeStore(
    (state) =>
      get(state.resume.data.sections, "skills", defaultSections.skills) as { items: Skill[] },
  );
  const items = section.items ?? [];
  const suggestions = useMemo(() => getSkillSuggestions(headline), [headline]);
  const [view, setView] = useState<View>(() => (items.length ? "list" : "form"));
  const [editing, setEditing] = useState<Skill>();
  const add = () => {
    setEditing(undefined);
    setView("form");
  };
  const save = (value: Skill) => {
    const item = { ...value, id: editing?.id ?? createId(), visible: editing?.visible ?? true };
    setValue(
      "sections.skills.items",
      editing
        ? items.map((current) => (current.id === editing.id ? item : current))
        : [...items, item],
    );
    setEditing(undefined);
    setView("list");
    toast({ variant: "success", title: editing ? "Compétence mise à jour" : "Compétence ajoutée" });
  };
  const remove = (id: string) => {
    setValue(
      "sections.skills.items",
      items.filter((item) => item.id !== id),
    );
    toast({ variant: "success", title: "Compétence supprimée" });
  };
  const reorder = (active: string, over: string) => {
    const from = items.findIndex((item) => item.id === active);
    const to = items.findIndex((item) => item.id === over);
    if (from >= 0 && to >= 0) setValue("sections.skills.items", arrayMove(items, from, to));
  };
  return (
    <section id="skills" className="mx-auto w-full max-w-[720px] pb-3">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
            Compétences
          </p>
          <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-indigo-950 sm:text-[28px]">
            Vos savoir-faire essentiels
          </h1>
          <p className="mt-1.5 max-w-lg text-xs leading-5 text-slate-500 sm:text-sm">
            Privilégiez les compétences attendues pour le poste ciblé.
          </p>
        </div>
        {view === "list" && (
          <button
            type="button"
            onClick={add}
            className="inline-flex min-h-10 w-fit items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800"
          >
            <PlusIcon size={15} weight="bold" /> Ajouter
          </button>
        )}
      </header>
      <div role="tablist" className="mt-5 flex rounded-xl bg-slate-100 p-1">
        <button
          type="button"
          disabled={!items.length}
          onClick={() => setView("list")}
          className={cn(
            "min-h-9 flex-1 rounded-lg text-[11px] font-bold",
            view === "list" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500",
            !items.length && "opacity-40",
          )}
        >
          Compétences{" "}
          <span className="ml-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px]">
            {items.length}
          </span>
        </button>
        <button
          type="button"
          onClick={add}
          className={cn(
            "min-h-9 flex-1 rounded-lg text-[11px] font-bold",
            view === "form" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500",
          )}
        >
          {editing ? "Modification" : "Nouvelle compétence"}
        </button>
      </div>
      <div className="mt-4">
        {view === "form" ? (
          <SkillForm
            item={editing}
            suggestions={suggestions}
            onCancel={() => {
              setEditing(undefined);
              setView(items.length ? "list" : "form");
            }}
            onSave={save}
          />
        ) : (
          <SkillList
            items={items}
            onAdd={add}
            onEdit={(item) => {
              setEditing(item);
              setView("form");
            }}
            onDelete={remove}
            onReorder={reorder}
          />
        )}
      </div>
    </section>
  );
};
